import { AGENT_EXTENSION_ID, AGENT_EXTENSION_MAIN_PORT_NAME, } from '#pplx/frontend/comet-agent-core/consts.js';
import { CometError } from '#pplx/frontend/comet/client/errors.js';
import { initExtension } from '#pplx/frontend/comet/client/extension.js';
import { AndroidBridgeAdapter, IosBridgeAdapter, } from '#pplx/frontend/comet/client/mobile/bridge.js';
import { createStore } from '#pplx/frontend/comet/client/store.js';
import { checkBrowserFaviconSupport, DEFAULT_API_CLASSIFIER_RESULT, DEFAULT_CLASSIFIER_API_TIMEOUT_MS, extractSearchPath, fetchClassifierResults, getCometBrowserInfo, getSidecarThreadId, isCometBrowser, sendClientPayloadAsync, sendToolFailureAsync, syncInlineAssistantParamsFromURL, } from '#pplx/frontend/comet/client/utils.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { reportAction, reportMetric, startTimer } from '#pplx/frontend/metrics/browser.js';
import { createNanoEvents } from 'nanoevents';
import { match } from 'ts-pattern';
const EXTENSION_ID = 'mcjlamohcooanphmebaiigheeeoplihb';
const STREAM_ID_PARAM = 'streamId';
const EMPTY_ARRAY = Object.freeze([]);
export class CometAdapter {
    tools;
    mobileProxy = new IosBridgeAdapter();
    isTestDebugMode = false;
    platform = 'web';
    store = createStore();
    mainExtension;
    agentExtension;
    taskTeardowns = new Map();
    emitter = createNanoEvents();
    // Preserve tool settings across server rename flow (remove -> add)
    pendingServerRename;
    classifierApiTimeoutMs = DEFAULT_CLASSIFIER_API_TIMEOUT_MS;
    managedOrganizationUUIDPromise;
    constructor() {
        const cometInfo = getCometBrowserInfo();
        if (cometInfo?.android_version) {
            this.platform = 'android';
        } // Not else to be compatible with current ios (set both fields)
        if (cometInfo?.ios_version) {
            this.platform = 'ios';
        }
        this.mainExtension = initExtension({
            onMessage: this.handleMessage,
            extensionId: EXTENSION_ID,
            name: 'entropy_client_context',
        });
        this.agentExtension = initExtension({
            onMessage: this.handleAgentExtensionMessage,
            extensionId: AGENT_EXTENSION_ID,
            name: AGENT_EXTENSION_MAIN_PORT_NAME,
        });
        this.tools = getBrowserCommonToolsService(this.agentExtension);
        if (this.platform === 'web') {
            this.mainExtension.sendMessage({ type: 'INIT' }).then((info) => {
                if (!info) {
                    return;
                }
                this.isTestDebugMode = Boolean(info.isTestDebugMode);
            }, (e) => {
                if (isCometBrowser()) {
                    throw e;
                }
            });
        }
        else {
            this.mobileProxy =
                this.platform === 'android' ? new AndroidBridgeAdapter() : new IosBridgeAdapter();
            this.fetchCurrentPageTab().catch((error) => {
                logger.error('[cometMobile] Error fetchCurrentPageTab', { error });
            });
            if (process.env.NODE_ENV === 'development') {
                // @ts-expect-error - assigned for debugging purposes
                window.nativeBridgeTest = this.mobileProxy.bridge;
            }
        }
        checkBrowserFaviconSupport();
    }
    getStore() {
        return this.store;
    }
    getMobileProxy() {
        return this.mobileProxy;
    }
    setClassifierApiTimeoutMs(timeoutMs) {
        this.classifierApiTimeoutMs = timeoutMs;
    }
    /**
     * Check if the agent is available
     * On mobile, agent is available when the native bridge is present
     * On desktop, agent is only available when extension is connected
     * @returns true if the agent is available, false otherwise
     */
    isAgentAvailable() {
        if (this.platform !== 'web') {
            return Boolean(this.mobileProxy.isAvailable);
        }
        return this.agentExtension.isConnected();
    }
    /**
     * Public method to open a new tab in Comet
     * @param href - The URL to open in a new tab
     * @returns Promise that resolves with the extension's response
     */
    async openNewTab(href) {
        const url = new URL(href);
        url.searchParams.delete('erp');
        url.pathname = url.pathname.replace('/sidecar', '');
        await this.openTab({
            url: url.toString(),
            request_id: 'openNewTab',
            key: 'openNewTab',
            navigation_history: false,
        });
    }
    connectAsi(payload) {
        return this.agentExtension.sendMessage({ type: 'CONNECT_ASI', payload });
    }
    async moveSidecarToThread(targetTabId) {
        try {
            const response = (await this.agentExtension.sendMessage({
                type: 'MOVE_SIDECAR_TO_THREAD',
                payload: { tab_id: targetTabId },
            }));
            return response.success;
        }
        catch (error) {
            logger.error('MOVE_SIDECAR_TO_THREAD failed', error);
            return false;
        }
    }
    /**
     * Public method to get navigation results from Comet.
     * This has _ prefix to indicate that it is not part of the public API.
     * This is only use for backwards compatibility.
     * @param streamId - The stream ID to get navigation results for
     * @returns Promise that resolves with the navigation results
     */
    _getNavigationResults(streamId) {
        return this.store.getState().navigationResults[streamId] ?? EMPTY_ARRAY;
    }
    /**
     * Public method to close the SideCar panel in Comet
     * @returns Promise that resolves with the extension's response
     */
    async closeSideCar(options = {}) {
        return this.send({
            type: 'HIDE_SIDECAR',
            payload: options,
        });
    }
    async openTab(payload) {
        if (this.platform !== 'web') {
            if (!this.mobileProxy?.isAvailable) {
                // Bridge unavailable (e.g. incognito mode) — fall back to standard navigation
                window.open(payload.url, '_blank');
                return {
                    is_success: true,
                    _metadata: { time: 1 },
                    result: {
                        tab_id: -1,
                        url: payload.url,
                        title: '',
                        last_accessed: 0,
                        visit_count: 1,
                    },
                };
            }
            const response = await this.mobileProxy.bridge.tabs_create({
                url: payload.url,
                hidden: false,
            });
            const openedTab = response.tab;
            return {
                is_success: true,
                _metadata: {
                    time: 1,
                },
                result: {
                    tab_id: openedTab.id,
                    url: openedTab.url ?? '',
                    title: openedTab.title ?? '',
                    last_accessed: openedTab.last_accessed ?? 0,
                    visit_count: 1,
                },
            };
        }
        const res = await this.tools.OpenTab({
            key: payload.key ?? 'unknown',
            request_id: payload.request_id ?? 'unknown',
            url: payload.url,
            tab_id: payload.tabId,
            navigation_history: payload.navigation_history,
        });
        return {
            is_success: true,
            result: res.tab,
        };
    }
    /**
     * Fetches navigation search results for mobile platform
     *
     * TEMPORARY SOLUTION: This method includes a parallel classifier API call
     * because the mobile backend doesn't yet provide classifier results.
     * This should be removed once the mobile backend is updated to include
     * classifier results in the navigation response.
     *
     * @param query - The search query
     * @param streamId - The stream identifier
     * @returns Promise resolving to navigation response with classifier results
     */
    async fetchMobileSearchResults(query, streamId) {
        const [navigationResults, classifierResult] = await Promise.all([
            this.mobileProxy.bridge.fetch_navigation_search_results({
                query,
                stream_id: streamId,
            }),
            fetchClassifierResults(query, this.classifierApiTimeoutMs).catch((error) => {
                logger.error('Classifier API call failed, using defaults', {
                    error,
                    query,
                    streamId,
                });
                return DEFAULT_API_CLASSIFIER_RESULT;
            }),
        ]);
        return {
            ...navigationResults.nav_search_response,
            classifierResult,
        };
    }
    /**
     * Unified method to fetch navigation results across platforms
     */
    async fetchNavigationResults(query, streamId) {
        if (this.platform === 'web') {
            const webPayload = await this.send({
                type: 'NAV_SEARCH',
                payload: { query, streamId },
            });
            if (!webPayload.is_success) {
                throw new Error('Failed to inject search results');
            }
            return webPayload;
        }
        else {
            const mobileResults = await this.fetchMobileSearchResults(query, streamId);
            return { ...mobileResults, _metadata: undefined };
        }
    }
    async injectSearchResults(query, streamId, telemetryData = {}) {
        try {
            const timer = startTimer();
            const extra = { streamId, ...telemetryData };
            reportAction('web.frontend.comet_navigation_run_inject', extra);
            this.store.getState().actions.createNavigationResultsTimer(streamId, telemetryData);
            this.store
                .getState()
                .actions.getNavigationResultsTimer(streamId)
                ?.addTimingOnce('web.frontend.comet_navigation_request_sent_ms');
            const navigationData = await this.fetchNavigationResults(query, streamId);
            this.store.getState().actions.addNavigationResults(streamId, navigationData);
            // non blocking call to save mobile navigation results to the sapi
            if (this.platform !== 'web' && navigationData.results?.length) {
                const cometState = this.store.getState();
                sendClientPayloadAsync({
                    clientPayloadCacheKey: `nav-${streamId}`,
                    cometBrowser: this,
                    reason: 'mobile_search_injection',
                    navigationResults: navigationData.results,
                    maxChars: 50000,
                    includeSidecar: false, // navigation results are not in sidecar
                    attachedTabs: [],
                    cometState,
                }).catch((error) => {
                    // Non-blocking: log error but don't interrupt the flow
                    logger.error('Failed to save mobile navigation results', {
                        streamId,
                        error,
                    });
                });
            }
            const type = navigationData.type ?? 'unknown';
            const timingExtra = { type, ...extra };
            timer.addTiming('web.frontend.comet_navigation_search_results', timingExtra);
            const startTime = timer.getAbsoluteStartTime();
            const metricExtra = { ...timingExtra, startTime };
            // Report timing metrics from navigationData (available on both platforms)
            if (navigationData.searchTime) {
                reportMetric('web.frontend.comet_navigation_search_time', {
                    duration: navigationData.searchTime,
                    ...metricExtra,
                });
            }
            if (navigationData.classifyTime) {
                reportMetric('web.frontend.comet_navigation_classify_time', {
                    duration: navigationData.classifyTime,
                    ...metricExtra,
                });
            }
            if (navigationData.networkingTime) {
                reportMetric('web.frontend.comet_navigation_networking_time', {
                    duration: navigationData.networkingTime,
                    ...metricExtra,
                });
            }
            // Report extension processing time (web platform only)
            if (this.platform === 'web' && navigationData._metadata?.time) {
                reportMetric('web.frontend.comet_navigation_extension_processing', {
                    duration: navigationData._metadata.time,
                    ...metricExtra,
                });
            }
        }
        catch (error) {
            logger.error(new CometError('NAV_RESULTS_INJECTION_FAILED', {
                details: { streamId },
                cause: error,
            }));
        }
    }
    async getIsCometDefaultBrowser(key) {
        try {
            const result = await this.send({
                type: 'GET_IS_DEFAULT_BROWSER',
                key,
            });
            if (result.is_success) {
                return result.isDefault;
            }
            else {
                return null;
            }
        }
        catch {
            return null;
        }
    }
    async setCometAsDefaultBrowser(key) {
        return this.send({ type: 'SET_AS_DEFAULT_BROWSER', key });
    }
    async getDidSkipCookieImportOnboarding(key) {
        const result = await this.send({
            type: 'GET_ONBOARDING_IMPORT_STATUS',
            key,
        });
        // if unknown (e.x. onboarded before we started tracking), assume they did not skip
        // only return true if we explicitly know they skipped
        const didSkip = result.importStatus === 'NOT_IMPORTED';
        logger.info('[getDidSkipCookieImportOnboarding]', didSkip);
        return didSkip;
    }
    notifyUserStateChanged(payload) {
        this.send({
            type: 'USER_STATE_CHANGED',
            payload,
        }).catch(() => {
            // do nothing
        });
    }
    subscribeCometNavigate(callback) {
        const handleNavigationEvent = (event) => {
            const parsedUrl = new URL(event.detail.url);
            syncInlineAssistantParamsFromURL({
                cometAdapter: this,
                urlSearchParams: parsedUrl.searchParams,
            });
            delete window.pplxNavIntentEvent;
            reportAction('comet_navigate');
            const DEFAULT_SEARCH_NEW_PATH = '/search/new';
            // Backward compatibility with Perplexity - AI Search extension
            // FIXME: https://linear.app/perplexity/issue/WEB-1570/fix-pplx-extension-to-use-searchnew
            // Remove this hack after changes in AI Search extension
            if (parsedUrl.pathname === '/search' && parsedUrl.searchParams.has('q')) {
                parsedUrl.pathname = DEFAULT_SEARCH_NEW_PATH;
            }
            let omniboxSearchPath = DEFAULT_SEARCH_NEW_PATH;
            if (import.meta.env.VITE_IS_SIDECAR_BUILD) {
                omniboxSearchPath = '/sidecar' + DEFAULT_SEARCH_NEW_PATH;
            }
            if (import.meta.env.VITE_IS_MOBILE_SIDECAR_BUILD) {
                omniboxSearchPath = '/mobile-sidecar' + DEFAULT_SEARCH_NEW_PATH;
            }
            if (import.meta.env.VITE_IS_INLINE_ASSISTANT_BUILD) {
                omniboxSearchPath = '/inline-assistant' + DEFAULT_SEARCH_NEW_PATH;
            }
            // For soft-nav into /search/new we want to make it "trusted" compared with hard-nav
            if (parsedUrl.pathname === omniboxSearchPath) {
                const streamId = safeRandomUUID();
                parsedUrl.searchParams.set(STREAM_ID_PARAM, streamId);
                // We need to allow unsecure tools for soft-nav by settings trusted input
                const omniboxInput = event.detail.text || '';
                this.store.getState().actions.setLastOmniboxInput(streamId, omniboxInput);
                // We don't need nav results for sidecar
                if (!import.meta.env.VITE_IS_SIDECAR_BUILD &&
                    !import.meta.env.VITE_IS_MOBILE_SIDECAR_BUILD &&
                    !import.meta.env.VITE_IS_INLINE_ASSISTANT_BUILD) {
                    const query = parsedUrl.searchParams.get('q') ?? '';
                    this.injectSearchResults(query, streamId, {
                        omnisearch: true,
                    });
                }
            }
            callback({ url: parsedUrl });
        };
        if (window.pplxNavIntentEvent) {
            handleNavigationEvent(window.pplxNavIntentEvent);
        }
        // @ts-expect-error custom event type
        document.addEventListener('comet-navigate', handleNavigationEvent);
        return () => {
            // @ts-expect-error custom event type
            document.removeEventListener('comet-navigate', handleNavigationEvent);
        };
    }
    subscribeToAgentStepUpdate() {
        const handleAgentStepUpdate = (event) => {
            this.sendThreadStatusToMobile({
                status: 'working',
                streamId: event.detail.streamId,
                displayDataId: event.detail.stepAction,
                allowClarifications: true,
            });
        };
        // @ts-expect-error custom event type
        document.addEventListener('comet-agent-step-update', handleAgentStepUpdate);
        return () => {
            // @ts-expect-error custom event type
            document.removeEventListener('comet-agent-step-update', handleAgentStepUpdate);
        };
    }
    subscribeToCometQuery(callback) {
        const handleQueryEvent = (event) => {
            const query = event.detail.query;
            delete window.pplxCometQuery;
            callback(query, {
                source: event.detail.source,
                quote: event.detail.quote,
                searchMode: event.detail.search_mode,
                model: event.detail.model,
                attachments: event.detail.attachments,
                tabs: event.detail.tabs,
                sources: event.detail.sources,
            });
        };
        if (window.pplxCometQuery) {
            handleQueryEvent(window.pplxCometQuery);
        }
        // @ts-expect-error custom event type
        document.addEventListener('comet-query', handleQueryEvent);
        return () => {
            // @ts-expect-error custom event type
            document.removeEventListener('comet-query', handleQueryEvent);
        };
    }
    getLastOmniboxInput(key) {
        return this.store.getState().omniboxInput[key];
    }
    async getCookies() {
        if (this.platform !== 'web') {
            const res = (await this.mobileProxy.bridge.agent_get_cookies()).base64_cookies;
            return { base64_cookies: res };
        }
        const response = await this.agentExtension.sendMessage({
            type: 'GET_ALL_COOKIES',
        });
        if (!response.success || !response.cookies) {
            throw new Error(response.error ?? 'Failed to get cookies');
        }
        const cookiesJson = JSON.stringify(response.cookies);
        const base64Cookies = btoa(cookiesJson);
        return { base64_cookies: base64Cookies };
    }
    async searchTabs(key) {
        if (this.platform !== 'web') {
            const { tabs } = await this.mobileProxy.bridge.tabs_query();
            return {
                is_success: true,
                tabs: tabs.map((tab) => {
                    return {
                        tabId: tab.id,
                        title: tab.title ?? '',
                        url: tab.url,
                        isCurrent: false, // Android does not support current tab detection
                        lastAccess: tab.last_accessed ?? 0,
                        snippet: '',
                    };
                }),
                _metadata: {
                    time: 1, // Fallback to 1ms for Android
                },
            };
        }
        const res = await this.tools.SearchBrowser({
            queries: [],
            sources: ['OPEN_TABS'],
            key,
            history_max_results: 0,
            open_tabs_max_results: 500,
            closed_tabs_max_results: 0,
            request_id: key,
        });
        return {
            is_success: true,
            tabs: (res.open_tabs_results ?? []).map((model) => ({
                ...model,
                url: model.url ?? '',
                title: model.title ?? '',
                tabId: model.tab_id ?? -1, // backward compatibility
                lastAccess: model.last_accessed ?? 0, // backward compatibility
            })),
        };
    }
    async ungroupTabs(payload, key, request_id) {
        const res = await this.tools.UngroupTabs({
            group_ids: payload.groupIds,
            tab_ids: payload.tabIds,
            key,
            request_id,
        });
        return {
            is_success: true,
            tabIds: (res.ungrouped_tabs ?? []).map((tab) => ({
                id: tab.tab_id ?? -1,
                error: tab.error,
            })),
            groupIds: payload.groupIds.map((id) => ({
                id,
            })),
        };
    }
    showNotification() {
        this.send({ type: 'SHOW_NOTIFICATION' }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    hideInlineAssistant() {
        this.send({ type: 'CLOSE_INLINE_ASSISTANT' }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    setInlineAssistantContentsSize(size) {
        this.send({
            type: 'SET_INLINE_ASSISTANT_CONTENTS_SIZE',
            payload: { size },
        }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    setInlineAssistantDraggableRegions(regions) {
        this.send({
            type: 'SET_INLINE_ASSISTANT_DRAGGABLE_REGIONS',
            payload: { regions },
        }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    addInlineAssistantDraggableRegion(region) {
        this.store.getState().actions.setInlineAssistantDraggableRegion(region);
        const regions = Array.from(this.store.getState().inlineAssistantDraggableRegions.values());
        this.setInlineAssistantDraggableRegions(regions);
    }
    removeInlineAssistantDraggableRegion(region) {
        this.store.getState().actions.removeInlineAssistantDraggableRegion(region);
        const regions = Array.from(this.store.getState().inlineAssistantDraggableRegions.values());
        this.setInlineAssistantDraggableRegions(regions);
    }
    sendThreadStatusToMobile(payload) {
        const { status, streamId, text, displayDataId, allowClarifications } = payload;
        this.mobileProxy.bridge
            .thread_status({
            type: status,
            text,
            stream_id: streamId,
            display_data_id: displayDataId,
            allow_clarifications: allowClarifications ?? false,
        })
            .catch((e) => {
            logger.error('Failed to send thread status to mobile bridge', e);
        });
    }
    sendVoiceStatusToMobile(payload) {
        const { status, text } = payload;
        this.mobileProxy.bridge
            .voice_status({
            type: status,
            text,
        })
            .catch((e) => {
            logger.error('Failed to send voice status to mobile bridge', e);
        });
    }
    openUserSettings() {
        if (this.platform !== 'web') {
            this.mobileProxy.bridge.open_user_settings().catch((e) => {
                logger.error('Failed to open user settings on mobile', e);
            });
        }
    }
    setNativeInputVisibility(visible) {
        if (this.platform !== 'web') {
            this.mobileProxy.bridge.set_native_input_visibility({ visible }).catch((e) => {
                logger.error('Failed to set native input visibility', e);
            });
        }
    }
    /**
     * Open a native sheet/modal in the mobile app (mobile only)
     * @param sheet - The native sheet to open: 'settings', 'upgrade', or 'login'
     */
    openNativeSheet(sheet) {
        if (this.platform !== 'web') {
            this.mobileProxy.bridge.app_navigate({ target: sheet }).catch((e) => {
                logger.error(`Failed to open native ${sheet} sheet`, e);
            });
        }
    }
    subscribeToMobileCommands(customHandler) {
        const handleMobileCommand = (event) => {
            if (event.detail.cmd === 'update-context') {
                this.store.getState().actions.setSourceTab({
                    secondaryTab: null, // we don't have split-view on Comet mobile
                    tabId: event.detail.tab.id,
                    title: event.detail.tab.title,
                    url: event.detail.tab.url,
                });
            }
            else if (customHandler) {
                customHandler(event.detail);
            }
            else if (event.detail.cmd === 'stop' && event.detail.subject === 'thread') {
                this.emitter.emit('BROWSER_TASK_STOP');
            }
            else {
                logger.warn('[comet-mobile] Unknown mobile command', event.detail.cmd);
            }
        };
        // @ts-expect-error custom event type
        document.addEventListener('comet-mobile-cmd', handleMobileCommand);
        return () => {
            // @ts-expect-error custom event type
            document.removeEventListener('comet-mobile-cmd', handleMobileCommand);
        };
    }
    setTabProgress(inProgress) {
        this.send({ type: 'SET_TAB_PROGRESS', payload: inProgress }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    /**
     * Pushes the embedded sidecar's thread/streaming state to the native shell so
     * the native ask input can toggle submit↔stop and surface follow-up context.
     * The extension handler (`SIDECAR_THREAD_STATE`) is defined on the Comet side.
     */
    setSidecarThreadState(state) {
        this.send({ type: 'SIDECAR_THREAD_STATE', payload: state }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    voiceAssistantUserSpeakingVADChanged(isSpeaking) {
        this.send({
            type: 'VOICE_ASSISTANT_USER_SPEAKING_VAD_CHANGED',
            payload: { isSpeaking },
        }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    voiceAssistantNotifyConnected() {
        this.send({
            type: 'VOICE_ASSISTANT_NOTIFY_CONNECTED',
        }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    voiceAssistantNotifyDisconnected() {
        this.send({
            type: 'VOICE_ASSISTANT_NOTIFY_DISCONNECTED',
        }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    voiceAssistantStop() {
        this.send({
            type: 'VOICE_ASSISTANT_STOP',
        }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    voiceAssistantShowWindow() {
        this.send({
            type: 'VOICE_ASSISTANT_SHOW_WINDOW',
        }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    voiceAssistantHideWindow() {
        this.send({
            type: 'VOICE_ASSISTANT_HIDE_WINDOW',
        }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    voiceAssistantSetDraggableRegions(regions) {
        this.send({
            type: 'VOICE_ASSISTANT_SET_DRAGGABLE_REGIONS',
            payload: { regions },
        }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    voiceAssistantSetWindowSize(size) {
        this.send({
            type: 'VOICE_ASSISTANT_SET_WINDOW_SIZE',
            payload: { size },
        }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    async searchTabGroups(payload, key, request_id) {
        const res = await this.tools.SearchTabGroups({
            queries: payload.queries,
            request_id,
            key,
        });
        return {
            is_success: true,
            results: (res.tab_groups ?? []).map((group) => ({
                ...group,
                id: group.id ?? -1,
                collapsed: group.collapsed ?? false,
                color: group.color,
                tabs: (group.tabs ?? []).map((tab) => ({
                    ...tab,
                    url: tab.url ?? '',
                    title: tab.title ?? '',
                    tabId: tab.tab_id ?? -1, // backward compatibility
                    isCurrent: tab.is_current_tab ?? false, // backward compatibility
                })),
            })),
        };
    }
    showFeedbackForm() {
        this.send({ type: 'SHOW_FEEDBACK_FORM' }).catch(() => {
            // Fire-n-forget so ignore
        });
    }
    subscribeScreenshotCaptured(callback) {
        return this.emitter.on('SCREENSHOT_CAPTURED', callback);
    }
    /**
     * Handle a message from the extension
     * @param message - The message to handle
     */
    handleAgentExtensionMessage = (message) => {
        try {
            match(message)
                .with({ type: 'SCREENSHOT_CAPTURED' }, async ({ payload: { screenshot } }) => {
                const res = await fetch(screenshot);
                const blob = await res.blob();
                const file = new File([blob], 'screenshot.png', {
                    type: blob.type,
                });
                logger.info('Screenshot captured');
                this.emitter.emit('SCREENSHOT_CAPTURED', file);
            })
                .with({ type: 'BROWSER_TASK_PROGRESS_SCREENSHOT' }, async ({ payload: { screenshot, taskUuid } }) => {
                this.store.getState().actions.addTaskScreenshot(taskUuid, screenshot);
            })
                .with({ type: 'ACTION_STARTED' }, ({ payload: { taskUuid, uuid, action } }) => {
                this.store.getState().actions.addComputerAction(uuid, {
                    action: action.action?.toLowerCase() ?? '',
                    text: action.text,
                    duration: action.duration,
                });
                const taskHandler = this.store.getState().actions.getPendingTask(taskUuid);
                if (taskHandler?.onProgress) {
                    taskHandler.onProgress({ type: 'ACTION_STARTED', uuid, action });
                }
            })
                .with({ type: 'BROWSER_TASK_PROGRESS' }, ({ payload: { taskUuid, progress } }) => {
                const taskHandler = this.store.getState().actions.getPendingTask(taskUuid);
                if (taskHandler?.onProgress) {
                    taskHandler.onProgress(progress);
                }
                this.emitter.emit('BROWSER_TASK_PROGRESS', taskUuid, progress);
            })
                .with({ type: 'BROWSER_TASK_COMPLETE' }, ({ payload: { taskUuid, result } }) => {
                const taskHandler = this.store.getState().actions.getPendingTask(taskUuid);
                if (taskHandler) {
                    this.store.getState().actions.deletePendingTask(taskUuid);
                    taskHandler.resolve(result);
                }
                this.emitter.emit('BROWSER_TASK_COMPLETE', taskUuid, result);
            })
                .with({ type: 'BROWSER_TASK_STOP' }, ({ payload }) => {
                logger.info('Browser task stopped');
                this.emitter.emit('BROWSER_TASK_STOP', payload.entryId);
            })
                .with({ type: 'BROWSER_TASK_PAUSE_RESUME_FRONTEND' }, ({ payload }) => {
                logger.info('Browser task paused/resumed');
                this.store.getState().actions.setTaskState(payload.task_uuid, payload.is_paused);
            })
                .with({ type: 'AGENT_TASK_FAILURE' }, ({ payload: { taskUuid, extraHeaders, error } }) => {
                const taskHandler = this.store.getState().actions.getPendingTask(taskUuid);
                if (taskHandler) {
                    this.store.getState().actions.deletePendingTask(taskUuid);
                    taskHandler.reject(new Error(error));
                }
                sendToolFailureAsync({
                    clientPayloadCacheKey: taskUuid,
                    errorType: error,
                    errorMsg: 'Agent task failure.',
                }, extraHeaders, 'CometAdapter').catch((e) => {
                    logger.error('Error sending tool failure', e);
                });
            })
                .with({ type: 'USER_SELECTION_CAPTURED' }, ({ payload: { selection } }) => {
                this.store.getState().actions.setUserSelection(selection);
            })
                .with({ type: 'ACTIVE_TAB_CHANGED' }, ({ payload }) => {
                this.store.getState().actions.setActiveTab(payload);
            })
                .with({ type: 'VOICE_ASSISTANT_ASSOCIATED_WINDOW_CHANGED' }, ({ payload: { windowId, activeTab } }) => {
                this.store.getState().actions.setVoiceAssistantAssociatedWindow(windowId);
                this.store.getState().actions.setVoiceAssistantActiveTab(activeTab);
            })
                .with({ type: 'MOVE_THREAD_TO_SIDECAR' }, ({ payload: { url, task_tab_ids, entry_id, thread_url_slug, auto_opened } }) => {
                if (auto_opened !== undefined) {
                    const currentSourceTab = this.store.getState().sidecarSourceTab;
                    const threadId = getSidecarThreadId(currentSourceTab.tabId, currentSourceTab.secondaryTab?.tabId);
                    this.store.getState().actions.setSidecarAutoOpened(auto_opened, threadId);
                }
                let urlStr;
                let urlObj;
                // Relative url
                if (thread_url_slug) {
                    urlObj = new URL(window.location.href);
                    urlObj.pathname = '/sidecar/search/' + thread_url_slug;
                    urlStr = urlObj.toString();
                }
                else if (entry_id) {
                    urlObj = new URL(window.location.href);
                    urlObj.pathname = '/sidecar/search/' + entry_id;
                    urlStr = urlObj.toString();
                }
                else {
                    // Absolute url
                    urlObj = new URL(url);
                    urlObj.pathname = '/sidecar' + extractSearchPath(urlObj.pathname);
                    urlStr = urlObj.toString();
                }
                task_tab_ids.forEach((tabId) => {
                    const threadId = getSidecarThreadId(tabId, undefined);
                    this.store.getState().actions.setSidecarUrlById(threadId, urlObj.pathname);
                });
                // Skip navigation if we're already on the thread page
                // to prevent the sidecar from flicking
                if (thread_url_slug && window.location.pathname.includes(thread_url_slug)) {
                    return;
                }
                document.dispatchEvent(new CustomEvent('comet-navigate', {
                    detail: { url: urlStr, text: '' },
                }));
            })
                .with({ type: 'GET_TASK_TABS' }, ({ payload: { tabs } }) => {
                this.store.getState().actions.setTaskTabs(tabs);
            })
                .with({ type: 'MCP_STDIO_SERVER_CHANGED' }, ({ payload: { serverName, changes, server } }) => {
                this.handleMcpStdioServerChanged(serverName, changes, server);
            })
                .with({ type: 'MCP_PERSISTED_STDIO_SERVERS_LOADED' }, () => {
                this.handleMcpPersistedStdioServersLoaded();
            })
                .with({ type: 'MCP_STDIO_SERVER_ADDED' }, ({ payload: { server } }) => {
                this.handleMcpStdioServerAdded(server);
            })
                .with({ type: 'MCP_STDIO_SERVER_REMOVED' }, ({ payload: { serverName } }) => {
                this.handleMcpStdioServerRemoved(serverName);
            })
                .otherwise((message) => {
                logger.log('Unknown message type', message);
            });
        }
        catch (error) {
            logger.error('Error parsing entropy message', error);
        }
    };
    async fetchOpenedTabs() {
        const tabs = await this.searchTabs('fetch_opened_tabs');
        if (!tabs.is_success)
            return [];
        return tabs.tabs
            .filter((tab) => (tab.url.startsWith('https://') || tab.url.startsWith('file://')) && !tab.isCurrent)
            .sort((a, b) => b.lastAccess - a.lastAccess);
    }
    async getTopMostVisitedUrls(payload, key) {
        return this.send({ type: 'GET_TOP_MOST_VISITED_URLS', payload, key });
    }
    getQuickActions(key) {
        return this.send({ type: 'GET_QUICK_ACTIONS', key });
    }
    async getHistorySummary(key, queries = [], browserHistorySummaryNumDays = 14, browserHistorySummaryMaxResults = 20) {
        return this.SearchBrowser({
            queries,
            sources: ['HISTORY'],
            key: key,
            request_id: '',
            start_time: browserHistorySummaryNumDays * 24 * 60 * 60 * 1000, // relative time in ms
            history_max_results: browserHistorySummaryMaxResults,
            open_tabs_max_results: browserHistorySummaryMaxResults,
            closed_tabs_max_results: browserHistorySummaryMaxResults,
        });
    }
    async GetContent(request) {
        if (this.platform !== 'web') {
            const res = (await Promise.allSettled((request.pages ?? []).map(async (page) => {
                if (!page.id) {
                    const response = await this.mobileProxy.bridge.tabs_get_url_content({
                        url: page.url ?? '',
                    });
                    return response.tab_content;
                }
                const response = await this.mobileProxy.bridge.tabs_get_content({
                    id: page.id,
                });
                return response.tab_content;
            })))
                .map((item) => (item.status === 'fulfilled' ? item.value : undefined))
                .filter((item) => item !== undefined);
            return {
                contents: res.map((result) => {
                    return {
                        html: '',
                        id: result.id,
                        url: result.url,
                        title: result.title ?? '',
                        og_meta: result.og_meta ?? {},
                        markdown: result.markdown ?? '', // Fallback to HTML for markdown
                    };
                }),
            };
        }
        return this.tools.GetContent({
            pages: (request.pages ?? []).map((p) => ({ id: p.id ?? -1, url: p.url ?? '' })),
            key: request.key ?? '',
            request_id: request.request_id ?? '',
            filter: request.filter ?? 'ALL',
        });
    }
    async GetVisibleTabScreenshot(request) {
        return this.tools.GetVisibleTabScreenshot(request);
    }
    async SearchBrowser(request) {
        const sources = request.sources ?? [];
        const queries = request.queries ?? [];
        if (this.platform !== 'web') {
            const historyPromise = sources.includes('HISTORY')
                ? Promise.allSettled((queries.length ? queries : ['']).map(async (text) => {
                    return await this.mobileProxy.bridge.history_search({
                        text,
                        max_results: request.history_max_results,
                        start_time: request.start_time,
                        end_time: request.end_time,
                    });
                }))
                : [];
            const [{ tabs }, history] = await Promise.all([
                sources.includes('OPEN_TABS') ? this.mobileProxy.bridge.tabs_query() : { tabs: [] },
                historyPromise,
            ]);
            const historyItems = history
                .map((item) => (item.status === 'fulfilled' ? item.value.history_items : []))
                .flat()
                .map((item) => ({
                title: item.title ?? '',
                url: item.url,
                last_accessed: item.last_visit_time ?? Date.now(),
                visit_count: item.visit_count ?? 1,
            }));
            return {
                closed_tabs_results: [],
                history_results: historyItems,
                open_tabs_results: tabs.map((tab) => ({
                    tab_id: tab.id,
                    title: tab.title ?? '',
                    url: tab.url ?? '',
                    last_accessed: tab.last_accessed ?? 0,
                    visit_count: 1,
                })),
            };
        }
        return this.tools.SearchBrowser(request);
    }
    async GetSidecarContext(request) {
        // Now in Comet Mobile we're using it only from Voice
        // Where we don't have active tab id as payload,
        // so we need to fetch activeTab and its content
        if (this.platform !== 'web') {
            const res = await this.mobileProxy.bridge.tabs_query();
            const lastActiveTab = res.tabs.sort((a, b) => (b.last_accessed ?? -1) - (a.last_accessed ?? -1))[0];
            if (!lastActiveTab) {
                return {
                    content: undefined,
                    contents: [],
                };
            }
            const mobileContent = (await this.mobileProxy.bridge.tabs_get_content({
                id: lastActiveTab?.id,
            })).tab_content;
            const content = {
                id: mobileContent.id,
                html: '',
                markdown: mobileContent.markdown ?? '',
                og_meta: mobileContent.og_meta ?? {},
                title: mobileContent.title ?? '',
                url: mobileContent.url ?? '',
            };
            return {
                content,
                contents: [content],
            };
        }
        return this.tools.GetSidecarContext({
            key: request.key ?? '',
            request_id: request.request_id ?? '',
        });
    }
    async closeTabs(payload, key, request_id) {
        if (this.platform !== 'web') {
            const { tabs } = await this.mobileProxy.bridge.tabs_query();
            const tabIdsToClose = payload.tabIds ?? [];
            const tabsToClose = tabs.filter((tab) => tabIdsToClose.includes(tab.id));
            tabsToClose.forEach((tab) => {
                this.mobileProxy.bridge.tabs_close({
                    id: tab.id,
                });
            });
            return {
                is_success: true,
                result: tabsToClose.map((tab) => ({
                    id: tab.id,
                    url: tab.url,
                    title: tab.title ?? '',
                })),
            };
        }
        const res = await this.tools.CloseTabs({
            tab_ids: (payload.tabIds ?? []),
            key,
            request_id,
        });
        return {
            is_success: true,
            result: (res.tabs ?? []).map((tab) => ({
                id: tab.tab_id ?? -1,
                url: tab.url ?? '',
                title: tab.title ?? '',
                ...tab,
            })),
        };
    }
    async groupTabs(payload, key, request_id) {
        const res = await this.tools.GroupTabs({
            ...payload,
            tab_ids: payload.tabIds,
            group_id: payload.groupId,
            key,
            request_id,
        });
        if (!res.tab_group)
            throw new Error('[GroupTabs] Tab group not found');
        return {
            is_success: true,
            tabGroup: {
                id: res.tab_group.id ?? -1,
                title: res.tab_group.title,
                collapsed: res.tab_group.collapsed ?? false,
                color: res.tab_group.color,
            },
        };
    }
    async maybeSendSearchResult(result, steps, isPending) {
        if (!this.isTestDebugMode || !result) {
            return;
        }
        const allWebResults = [...(result.web_results ?? []), ...(result.extra_web_results ?? [])];
        const usefulWebResults = allWebResults.map((result) => {
            return {
                name: result.name,
                snippet: result.snippet,
                url: result.url,
                isClientContext: Boolean(result.is_client_context),
            };
        });
        return this.send({
            type: 'PERPLEXITY_SEARCH_RESULT',
            payload: {
                answer: result.answer,
                webResults: usefulWebResults,
                isPending,
                // We need to figure out which steps were passed e.g. for agents, to differ them from default client context
                steps: steps.map((step) => step.step_type),
            },
        });
    }
    /**
     * Public method to submit a task to Comet extension
     */
    submitTask(payload, onProgress) {
        return new Promise((resolve, reject) => {
            const entryId = payload.entryId;
            const taskId = payload.uuid ?? '';
            this.store.getState().actions.setPendingTask(taskId, {
                entryId: payload.entryId,
                uuid: taskId,
                resolve,
                reject,
                onProgress,
            });
            const teardowns = this.taskTeardowns.get(entryId) ?? [];
            this.taskTeardowns.set(entryId, teardowns);
            if (this.platform !== 'web') {
                const cometInfo = getCometBrowserInfo();
                logger.info('[comet] Starting native agent', {
                    entryId,
                    uuid: payload.uuid,
                    platform: this.platform,
                    task: payload.task,
                    androidVersion: cometInfo?.android_version,
                });
                // Register teardown for cleanup
                teardowns.push((reason) => {
                    logger.info('[comet] Native agent task finished', {
                        entryId,
                        uuid: payload.uuid,
                        platform: this.platform,
                        reason,
                        task: payload.task,
                        androidVersion: cometInfo?.android_version,
                    });
                    this.mobileProxy.bridge.agent_stop({ entry_id: entryId }).catch((err) => {
                        logger.error('[comet] Native agent stop failed', {
                            error: err,
                            entryId,
                            platform: this.platform,
                            uuid: payload.uuid,
                            task: payload.task,
                            androidVersion: cometInfo?.android_version,
                        });
                    });
                    this.store.getState().actions.setTaskState(taskId, undefined);
                });
                const handleNativeAgentStartFailure = (error) => {
                    logger.error('[comet] Native agent start failed', {
                        error,
                        entryId,
                        uuid: payload.uuid,
                        platform: this.platform,
                        androidVersion: cometInfo?.android_version,
                    });
                    let extraHeaders = {};
                    try {
                        extraHeaders = JSON.parse(payload.extra_headers ?? '{}');
                    }
                    catch {
                        // pass
                    }
                    sendToolFailureAsync({
                        clientPayloadCacheKey: taskId,
                        errorType: 'native_agent_unavailable',
                        errorMsg: 'Browser agent is unavailable, you should update the app',
                    }, extraHeaders, 'CometAdapter').catch((e) => {
                        logger.error('Error sending tool failure', e);
                    });
                    reject(error instanceof Error ? error : new Error(String(error)));
                };
                this.mobileProxy.bridge
                    .agent_start({
                    task: payload.task ?? '',
                    start_url: payload.start_url ?? '',
                    uuid: taskId,
                    tab_id: payload.tab_id ?? -1,
                    base_url: payload.base_url ?? '',
                    extra_headers: payload.extra_headers ?? '{}',
                    entry_id: payload.entryId,
                })
                    .then((res) => {
                    if (res.success) {
                        resolve({ message: 'Native agent started', success: true });
                    }
                    else {
                        handleNativeAgentStartFailure(new Error('Native agent failed to start'));
                    }
                })
                    .catch(handleNativeAgentStartFailure);
                return;
            }
            this.agentExtension
                .sendMessage(payload)
                .then(() => {
                teardowns.push((reason) => {
                    this.agentExtension.sendMessage({
                        type: 'CLEANUP_AGENT_TASKS',
                        payload: { entryId: payload.entryId, reason },
                    });
                });
            })
                .catch((err) => {
                let extraHeaders = {};
                try {
                    extraHeaders = JSON.parse(payload.extra_headers ?? '{}');
                }
                catch {
                    // pass
                }
                logger.error('[comet] Unhandled error while submitting task', err);
                sendToolFailureAsync({
                    clientPayloadCacheKey: taskId,
                    errorType: err,
                    errorMsg: 'Agent task start failure.',
                }, extraHeaders, 'CometAdapter').catch((e) => {
                    logger.error('Error sending tool failure', e);
                });
            });
        });
    }
    async moveThreadToSidecar({ activeTaskUuid, entryId, isSubagent, reason, animate, takeFocus, autoOpened = true, }) {
        try {
            const response = (await this.agentExtension.sendMessage({
                type: 'MOVE_THREAD_TO_SIDECAR',
                payload: {
                    active_task_uuid: activeTaskUuid,
                    is_subagent: isSubagent,
                    entry_id: entryId,
                    animate,
                    takeFocus,
                    auto_opened: autoOpened,
                },
            }));
            return response.success;
        }
        catch (error) {
            logger.error(new CometError('MOVE_THREAD_TO_SIDECAR_FAILED', {
                cause: error,
                message: `Failed to move thread to sidecar for ${reason}`,
            }));
            return false;
        }
    }
    getTaskTabs() {
        this.agentExtension.sendMessage({
            type: 'GET_TASK_TABS',
        });
    }
    async openYoutubeVideo(url, reason) {
        try {
            const response = (await this.agentExtension.sendMessage({
                type: 'OPEN_YOUTUBE_VIDEO',
                payload: { url },
            }));
            return response.success;
        }
        catch (error) {
            logger.error(new CometError('OPEN_YOUTUBE_VIDEO_FAILED', {
                cause: error,
                message: `Failed to open youtube reason for ${reason}`,
            }));
            return false;
        }
    }
    async sendMissionControlStatus(payload) {
        await this.agentExtension.sendMessage({
            type: 'MISSION_CONTROL_STATUS',
            payload,
        });
    }
    /**
     * @deprecated - old agent is disabled
     */
    async makeTaskVisible(taskUuid) {
        const result = (await this.agentExtension.sendMessage({
            type: 'MAKE_TASK_VISIBLE',
            payload: { task_uuid: taskUuid },
        }));
        if (result.tab_ids) {
            const currentPathname = new URL(window.location.href).pathname;
            result.tab_ids.forEach((tabId) => {
                this.store
                    .getState()
                    .actions.setSidecarUrlById(getSidecarThreadId(tabId, undefined), currentPathname);
            });
        }
    }
    cleanupTasks(entryId, reason) {
        if (!entryId) {
            return;
        }
        const teardowns = this.taskTeardowns.get(entryId);
        if (!teardowns) {
            // There is a case in sidecar when teardowns were set on NTP and NTP was closed before actual task/stream ended
            if (this.platform === 'web') {
                this.agentExtension.sendMessage({
                    type: 'CLEANUP_AGENT_TASKS',
                    payload: { entryId, reason },
                });
            }
            else {
                this.mobileProxy.bridge
                    .agent_stop({
                    entry_id: entryId,
                })
                    .catch((err) => {
                    logger.error('[comet] Native agent stop failed', {
                        error: err,
                        entryId,
                    });
                });
            }
            return;
        }
        teardowns.forEach((teardown) => teardown(reason));
        this.taskTeardowns.delete(entryId);
    }
    captureSourceTabScreenshotV2({ sourceTabUrl = '' }) {
        this.agentExtension.sendMessage({
            type: 'CAPTURE_SCREENSHOT_V2',
            // TODO: use tab ID for more stable results
            // currently using tab URL is a temporary workaround until the next
            // browser update
            payload: { tabId: undefined, sourceTabUrl },
        });
    }
    deactivateScreenshotTool() {
        this.agentExtension.sendMessage({
            type: 'DEACTIVATE_SCREENSHOT_TOOL',
            payload: { tabId: undefined },
        });
    }
    subscribeToSidecarBrowserTaskStop(callback) {
        return this.emitter.on('BROWSER_TASK_STOP', callback);
    }
    subscribeForceSubmitQuery(callback) {
        return this.emitter.on('FORCE_SUBMIT_QUERY', callback);
    }
    subscribeQuickActionButtonFired(callback) {
        return this.emitter.on('QUICK_ACTION_BUTTON_FIRED', callback);
    }
    subscribeVoiceAssistantStarted(callback) {
        return this.emitter.on('VOICE_ASSISTANT_STARTED', callback);
    }
    subscribeVoiceAssistantStopped(callback) {
        return this.emitter.on('VOICE_ASSISTANT_STOPPED', callback);
    }
    subscribeVoiceAssistantMuteMicRequested(callback) {
        return this.emitter.on('VOICE_ASSISTANT_MUTE_MIC_REQUESTED', callback);
    }
    subscribeVoiceAssistantUnmuteMicRequested(callback) {
        return this.emitter.on('VOICE_ASSISTANT_UNMUTE_MIC_REQUESTED', callback);
    }
    subscribeSourceTabChanged(callback) {
        return this.emitter.on('SOURCE_TAB_CHANGED', callback);
    }
    getMcpTools(serverName) {
        return this.sendToAgent({
            type: 'GET_MCP_TOOLS',
            payload: { serverName },
        });
    }
    callMcpTool(payload, context) {
        return this.sendToAgent({
            type: 'CALL_MCP_TOOL',
            payload,
            context,
        });
    }
    getMcpStdioServers() {
        const result = this.sendToAgent({
            type: 'GET_STDIO_MCP_SERVERS',
            payload: undefined,
        });
        result
            .then((servers) => {
            this.getStore().getState().actions.setMcpStdioServers(servers);
        })
            .catch(() => {
            // suppress error from MCP
        });
        return result;
    }
    addMcpStdioServer(server) {
        return this.sendToAgent({
            type: 'ADD_STDIO_MCP_SERVER',
            payload: server,
        });
    }
    async updateMcpStdioServer(existingServerName, updates) {
        try {
            return await this.sendToAgent({
                type: 'UPDATE_STDIO_MCP_SERVER',
                payload: { existingServerName, updates },
            });
        }
        catch (e) {
            // Temporary fallback to old logic this browser doesn't having
            // matching API.
            if (!(e instanceof Error) ||
                (!e.message.startsWith('Error in invocation of perplexity.mcp.updateStdioServer') &&
                    !e.message.startsWith('chrome.perplexity.mcp.updateStdioServer is not a function'))) {
                throw e;
            }
            // we have to remove the server first because
            // the browser doesn't have update method
            if (updates.name && updates.name !== existingServerName) {
                this.pendingServerRename = {
                    oldName: existingServerName,
                    newName: updates.name,
                };
            }
            else {
                this.pendingServerRename = undefined;
            }
            await this.removeMcpStdioServer(existingServerName, true);
            return this.addMcpStdioServer(updates);
        }
    }
    async removeMcpStdioServer(serverName, isAfterUpdate) {
        await this.sendToAgent({
            type: 'REMOVE_STDIO_MCP_SERVER',
            payload: { name: serverName },
        });
        this.clearStateAfterRemovingMcpServer(serverName, isAfterUpdate);
    }
    installDxt(url) {
        return this.sendToAgent({
            type: 'INSTALL_DXT',
            payload: { url },
        });
    }
    async uninstallDxt(name) {
        await this.sendToAgent({
            type: 'UNINSTALL_DXT',
            payload: { name },
        });
        this.clearStateAfterRemovingMcpServer(name);
    }
    getInstalledDxts() {
        const result = this.sendToAgent({
            type: 'GET_INSTALLED_DXT',
        });
        result
            .then((servers) => {
            this.getStore().getState().actions.setInstalledDxts(servers);
        })
            .catch(() => {
            // suppress error from MCP
        });
        return result;
    }
    async refreshDxtAndMcpState() {
        await Promise.all([this.getMcpStdioServers(), this.getInstalledDxts()]);
    }
    async hasPermission(permission) {
        try {
            const result = await this.sendToAgent({
                type: 'HAS_PERMISSION',
                payload: { permission },
            });
            return result;
        }
        catch {
            return null;
        }
    }
    async requestPermission(permission) {
        try {
            const result = await this.sendToAgent({
                type: 'REQUEST_PERMISSION',
                payload: { permission },
            });
            return result;
        }
        catch {
            return null;
        }
    }
    getManagedOrganizationUUIDPromise() {
        if (!this.managedOrganizationUUIDPromise) {
            this.managedOrganizationUUIDPromise = this.sendToAgent({
                type: 'GET_MANAGED_ORGANIZATION_UUID',
            }).catch(() => null);
        }
        return this.managedOrganizationUUIDPromise;
    }
    /**
     * Handle MCP stdio server change events from the extension
     */
    handleMcpStdioServerChanged(serverName, _changes, serverInfo) {
        const state = this.getStore().getState();
        const servers = state.mcpStdioServers ?? [];
        const index = servers.findIndex((server) => server.name === serverName);
        if (index === -1)
            return;
        const updatedServers = [...servers];
        updatedServers[index] = serverInfo;
        state.actions.setMcpStdioServers(updatedServers);
    }
    /**
     * Handle MCP persisted stdio servers loaded event from the extension
     */
    async handleMcpPersistedStdioServersLoaded() {
        await this.getMcpStdioServers();
    }
    /**
     * Handle MCP stdio server added event from the extension
     */
    handleMcpStdioServerAdded(serverInfo) {
        const state = this.getStore().getState();
        const servers = state.mcpStdioServers ?? [];
        const existingIndex = servers.findIndex((server) => server.name === serverInfo.name);
        if (existingIndex !== -1)
            return;
        const updatedServers = [...servers, serverInfo];
        state.actions.setMcpStdioServers(updatedServers);
    }
    /**
     * Handle MCP stdio server removed event from the extension
     */
    handleMcpStdioServerRemoved(serverName) {
        const state = this.getStore().getState();
        const servers = state.mcpStdioServers ?? [];
        const updatedServers = servers.filter((server) => server.name !== serverName);
        state.actions.setMcpStdioServers(updatedServers);
    }
    async fetchCurrentPageTab() {
        if (this.platform === 'web')
            return;
        const res = await this.mobileProxy.bridge.tabs_query();
        const lastActiveTab = res.tabs.sort((a, b) => (b.last_accessed ?? -1) - (a.last_accessed ?? -1))[0];
        if (!lastActiveTab)
            return;
        this.store.getState().actions.setSourceTab({
            tabId: lastActiveTab.id,
            title: lastActiveTab.title ?? '',
            url: lastActiveTab.url,
            secondaryTab: null,
        });
    }
    /**
     * Sends a message to the Comet extension
     * @param message - The message to send
     * @returns Promise that resolves with the extension's response
     */
    async send(message) {
        return this.mainExtension.sendMessage(message).then((response) => {
            // @ts-expect-error we don't know response type
            response.is_success = !response.errorType;
            return response;
        });
    }
    async sendToAgent(message) {
        return this.agentExtension.sendMessage(message).then((response) => {
            if (!response.success) {
                throw new Error(response.error);
            }
            return response.response;
        });
    }
    /**
     * Handle a message from the extension
     * @param message - The message to handle
     */
    handleMessage = (message) => {
        try {
            const parsedMessage = message;
            match(parsedMessage)
                .with({ type: 'UPDATE_SOURCE_TAB' }, (message) => {
                this.store.getState().actions.setSourceTab(message.payload);
                const shouldClearSelection = message.payload.url !== this.store.getState().sidecarSourceTab.url;
                if (shouldClearSelection) {
                    this.store.getState().actions.clearUserSelection();
                }
                this.emitter.emit('SOURCE_TAB_CHANGED');
            })
                .with({ type: 'FORCE_SUBMIT_QUERY' }, (message) => {
                if (!this.isTestDebugMode)
                    return;
                this.emitter.emit('FORCE_SUBMIT_QUERY', message.payload);
            })
                .with({ type: 'QUICK_ACTION_BUTTON_FIRED' }, () => {
                this.emitter.emit('QUICK_ACTION_BUTTON_FIRED');
            })
                .with({ type: 'VOICE_ASSISTANT_STARTED' }, () => {
                this.emitter.emit('VOICE_ASSISTANT_STARTED');
            })
                .with({ type: 'VOICE_ASSISTANT_STOPPED' }, () => {
                this.emitter.emit('VOICE_ASSISTANT_STOPPED');
            })
                .with({ type: 'VOICE_ASSISTANT_MUTE_MIC_REQUESTED' }, () => {
                this.emitter.emit('VOICE_ASSISTANT_MUTE_MIC_REQUESTED');
            })
                .with({ type: 'VOICE_ASSISTANT_UNMUTE_MIC_REQUESTED' }, () => {
                this.emitter.emit('VOICE_ASSISTANT_UNMUTE_MIC_REQUESTED');
            })
                .with({ type: 'EDITABLE_ELEMENT_FOCUS_STATE' }, (message) => {
                this.store.getState().actions.setInlineAssistantParams({
                    canInsert: message.payload.canInsert,
                });
            })
                .with({
                type: 'INIT',
            }, (message) => {
                this.isTestDebugMode = Boolean(message.payload.isTestDebugMode);
                this.store.getState().actions.setPlatformInfo(message.payload.platform);
                const actions = this.store.getState().actions;
                actions.setSourceTab({
                    tabId: message.payload.sourceTabId ?? -1,
                    url: message.payload.sourceTabUrl ?? '',
                    title: message.payload.sourceTabTitle ?? '',
                    secondaryTab: message.payload.sourceSecondaryTab ?? null,
                });
                if (message.payload.selection) {
                    actions.setUserSelection(message.payload.selection);
                }
            })
                .otherwise((message) => {
                logger.log('Unknown message type', message);
            });
        }
        catch (error) {
            logger.error('Error parsing entropy message', error);
        }
    };
    clearStateAfterRemovingMcpServer(serverName, isAfterUpdate) {
        const state = this.getStore().getState();
        const disabledServers = state.mcpDisabledServers ?? new Set();
        if (disabledServers.has(serverName)) {
            const next = new Set(disabledServers);
            next.delete(serverName);
            state.actions.setMcpDisabledServers(next);
        }
        if (isAfterUpdate && !this.pendingServerRename)
            return;
        // If this removal is part of a rename flow, rename settings instead of deleting
        if (this.pendingServerRename && this.pendingServerRename.oldName === serverName) {
            state.actions.renameMcpServerInToolsSettings(this.pendingServerRename.oldName, this.pendingServerRename.newName);
            this.pendingServerRename = undefined;
            return;
        }
        const toolsSettings = state.mcpToolsSettings ?? {};
        if (toolsSettings[serverName]) {
            const next = { ...toolsSettings };
            delete next[serverName];
            state.actions.setMcpToolsSettings(next);
        }
    }
    async getPrivacyInfo(key) {
        try {
            const result = await this.send({
                type: 'GET_PRIVACY_INFO',
                key,
            });
            if (result.is_success) {
                return result;
            }
            else {
                return null;
            }
        }
        catch {
            return null;
        }
    }
    async insertInlineAssistantSelection(key, text) {
        try {
            const result = await this.agentExtension.sendMessage({
                type: 'INSERT_INLINE_TEXT',
                payload: { text },
                key,
            });
            return result.success;
        }
        catch {
            return false;
        }
    }
    async getSiteOnboardingStatus(key) {
        try {
            const response = await this.mainExtension.sendMessage({
                type: 'GET_SITE_ONBOARDING_STATUS',
                key,
            });
            if (response.started === null) {
                return undefined;
            }
            return response.started;
        }
        catch {
            return undefined;
        }
    }
    async setSiteOnboardingStatus(key, started) {
        try {
            await this.mainExtension.sendMessage({
                type: 'SET_SITE_ONBOARDING_STATUS',
                key,
                payload: { started },
            });
        }
        catch {
            // Silently ignore errors
        }
    }
}
const getBrowserCommonToolsService = (extension) => new Proxy({}, {
    get(_target, method) {
        return (request) => extension
            .sendMessage({ type: 'CALL_TOOL', method, request })
            .then(({ success, response }) => {
            if (!success)
                throw response;
            return response;
        })
            .catch((error) => {
            logger.error('Error calling tool', error);
            if ('errorType' in error)
                throw error;
            // TODO: remove this once all users have updated the extension
            throw { errorType: 'unhandled', errorMsg: 'Proxy error.' };
        });
    },
});
//# sourceMappingURL=adapter.js.map